import React, { useMemo } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay, Pagination } from 'swiper/modules'
import { Card } from '@/components/Cards'
import { IPlaylistScript } from '@/types/cinematics'
import { IPlaylistItem } from '@/types'
import { SwiperOptions } from 'swiper/types'
import { useWindowDimensions } from '@/hooks/utils/useWindowDimensions'
import { useIdleReady } from '@/hooks/utils/useIdleReady'

import classNames from 'classnames'
import classes from './styles.module.scss'
import 'swiper/css'
import 'swiper/css/autoplay'
import 'swiper/css/pagination'

interface CarouselProps {
  items: IPlaylistItem[]
  options: IPlaylistScript['options']
  playlistScript: Pick<IPlaylistScript, '_id'>
  isPlaceholder?: boolean
}

interface TopSliderConfig {
  options: IPlaylistScript['options']
  totalItems: number
  isPlaceholder?: boolean
  isExtraLargeDesktop?: boolean
}

interface SliderOptions extends SwiperOptions {
  slidesPerView: number
}

const getSliderConfig = ({
  options,
  totalItems,
  isPlaceholder,
  isExtraLargeDesktop
}: TopSliderConfig): SliderOptions => {
  const hasDynamicBullets = totalItems > 5

  return {
    loop: hasDynamicBullets,
    pagination: {
      clickable: true,
      bulletClass: classes.bullet,
      bulletActiveClass: classes['bullet--active'],
    },
    autoplay: isPlaceholder ? false : options?.autoplay ?? {
      delay: 5000,
      disableOnInteraction: false
    },
    centeredSlides: true,
    speed: options?.speed ?? 600,
    preventInteractionOnTransition: true,
    spaceBetween: options?.spaceBetween ?? 20,
    effect: options?.effect ?? 'slide',
    rewind: options?.rewind ?? true,
    allowTouchMove: options?.drag ?? true,
    longSwipes: options?.longSwipes ?? true,
    shortSwipes: options?.shortSwipes ?? true,
    threshold: options?.threshold ?? 1,
    slidesPerView: isExtraLargeDesktop ? 1 : 1.05,
    initialSlide: 0,
    grabCursor: true,
  }
}

export const TopBannerCarousel = (props: CarouselProps) => {
  const {
    items,
    options,
    isPlaceholder
  } = props
  const totalItems = items?.length || 0
  const { isExtraLargeDesktop } = useWindowDimensions()
  const isIdleReady = useIdleReady()

  const sliderConfig = useMemo(() => getSliderConfig({ options, totalItems, isPlaceholder, isExtraLargeDesktop }), [options, totalItems, isPlaceholder, isExtraLargeDesktop])

  if (isPlaceholder) {
    return (
      <div className={classes['carousel--placeholder']}>
        <Card
          item={null}
          options={options}
          isPlaceholder={isPlaceholder}
        />
      </div>
    )
  }

  return (
    <div className={classNames({
      [classes.carousel]: true,
      [classes['carousel--extra-large-desktop']]: isExtraLargeDesktop,
    })}>
      <Swiper
        modules={[Autoplay, Pagination]}
        {...sliderConfig}
      >
        {items?.map((item, index) => (
          <SwiperSlide key={item._id ?? index}>
            <Card
              item={item}
              options={options}
              itemIndex={index}
              isPlaceholder={isPlaceholder}
              shouldLoadImage={index === 0 || isIdleReady}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </div >
  )
}